import React, { useState, useEffect } from 'react';
import { Search, Shield, ChefHat, Store, Crown, Users as UsersIcon, Eye, Building2, Mail } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { toast } from 'sonner';
import api from '../../../services/api';

const PERMISSION_CHIPS = [
  { key: 'can_place_orders', label: 'Orders' },
  { key: 'can_view_invoices', label: 'Invoices' },
  { key: 'can_pay_invoices', label: 'Pay' },
  { key: 'can_edit_profile', label: 'Profile' },
  { key: 'can_manage_users', label: 'Team' },
  { key: 'can_view_reports', label: 'Reports' },
];

const typeMeta = {
  system: { label: 'System', className: 'bg-red-100 text-red-800', icon: <Shield className="h-3.5 w-3.5" /> },
  client_owner: { label: 'Account Owner', className: 'bg-amber-100 text-amber-800', icon: <Crown className="h-3.5 w-3.5" /> },
  client_team_member: { label: 'Team Member', className: 'bg-blue-100 text-blue-800', icon: <UsersIcon className="h-3.5 w-3.5" /> },
};

const SystemRoleIcon = ({ role }) => {
  if (role === 'admin') return <Shield className="h-4 w-4 text-red-500" />;
  if (role === 'bakery_staff') return <ChefHat className="h-4 w-4 text-orange-500" />;
  return <Store className="h-4 w-4 text-blue-500" />;
};

export const AllUsersTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/admin/users/all');
        setRows(res.data);
      } catch {
        toast.error('Failed to load user directory');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = rows.filter((u) => {
    const q = search.toLowerCase();
    const matchesSearch =
      u.name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      (u.company_name || '').toLowerCase().includes(q);
    const matchesType = typeFilter === 'all' || u.user_type === typeFilter;
    return matchesSearch && matchesType;
  });

  const counts = {
    total: rows.length,
    system: rows.filter((u) => u.user_type === 'system').length,
    owners: rows.filter((u) => u.user_type === 'client_owner').length,
    members: rows.filter((u) => u.user_type === 'client_team_member').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48" data-testid="all-users-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="all-users-tab">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-gray-50"><CardContent className="p-4">
          <div className="text-sm text-gray-500">All Users</div>
          <div className="text-2xl font-bold" data-testid="all-users-total">{counts.total}</div>
        </CardContent></Card>
        <Card className="bg-red-50"><CardContent className="p-4">
          <div className="text-sm text-red-600 flex items-center gap-1"><Shield className="h-4 w-4" /> System</div>
          <div className="text-2xl font-bold text-red-700">{counts.system}</div>
        </CardContent></Card>
        <Card className="bg-amber-50"><CardContent className="p-4">
          <div className="text-sm text-amber-600 flex items-center gap-1"><Crown className="h-4 w-4" /> Owners</div>
          <div className="text-2xl font-bold text-amber-700">{counts.owners}</div>
        </CardContent></Card>
        <Card className="bg-blue-50"><CardContent className="p-4">
          <div className="text-sm text-blue-600 flex items-center gap-1"><UsersIcon className="h-4 w-4" /> Team Members</div>
          <div className="text-2xl font-bold text-blue-700">{counts.members}</div>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by name, email, or company..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="all-users-search"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-52" data-testid="all-users-type-filter">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="system">System (Admin/Staff)</SelectItem>
            <SelectItem value="client_owner">Account Owners</SelectItem>
            <SelectItem value="client_team_member">Team Members</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-4 font-medium text-gray-600">User</th>
                <th className="text-left p-4 font-medium text-gray-600">Type</th>
                <th className="text-left p-4 font-medium text-gray-600">Company / Account</th>
                <th className="text-left p-4 font-medium text-gray-600">Access</th>
                <th className="text-left p-4 font-medium text-gray-600">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const meta = typeMeta[u.user_type] || typeMeta.system;
                return (
                  <tr key={`${u.user_type}-${u.id}`} className="border-b hover:bg-gray-50" data-testid={`all-user-row-${u.id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                          <SystemRoleIcon role={u.system_role} />
                        </div>
                        <div>
                          <div className="font-medium flex items-center gap-2">
                            {u.name}
                            {u.pending_invite && (
                              <Badge className="bg-amber-100 text-amber-700 text-xs flex items-center gap-1">
                                <Mail className="h-3 w-3" /> Invite pending
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <Badge className={`${meta.className} flex items-center gap-1 w-fit`}>
                        {meta.icon}<span>{meta.label}</span>
                      </Badge>
                    </td>
                    <td className="p-4">
                      {u.company_name ? (
                        <div className="flex items-center gap-1.5 text-sm text-gray-700">
                          <Building2 className="h-3.5 w-3.5 text-gray-400" />
                          {u.company_name}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">—</span>
                      )}
                      {u.account_role && (
                        <div className="text-xs text-gray-500 mt-0.5 capitalize">Role: {u.account_role}</div>
                      )}
                    </td>
                    <td className="p-4">
                      {u.permissions ? (
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          {PERMISSION_CHIPS.filter((c) => u.permissions[c.key]).map((c) => (
                            <span key={c.key} className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5">
                              {c.label}
                            </span>
                          ))}
                          {PERMISSION_CHIPS.every((c) => !u.permissions[c.key]) && (
                            <span className="text-xs text-gray-400">No capabilities</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-500 flex items-center gap-1"><Eye className="h-3 w-3" /> Full system access</span>
                      )}
                    </td>
                    <td className="p-4">
                      <Badge className={u.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                        {u.status}
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="text-center py-8 text-gray-500" data-testid="all-users-empty">No users found</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AllUsersTab;
